import { useMemo } from "react";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@nosible/shared/components/ui/chart";
import {
  AreaChart,
  Area,
  YAxis,
  CartesianGrid,
} from "recharts";
import { trpc } from "@/account/lib/trpc";
import { useSession } from "@/account/lib/auth-client";

interface SubTotalUsageProps {
  subscriptionId: string;
}

type UsageDataPoint = {
  date: string;
  timestamp: number;
  total: number;
  [key: string]: string | number;
};

function formatCompactNumber(value: number): string {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: value >= 1_000_000 ? 1 : 0,
  }).format(value);
}

function formatChartDate(timestamp: number, edge: "first" | "last"): string {
  const date = new Date(timestamp);
  if (edge === "first") {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      year: "numeric",
    }).format(date);
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
  }).format(date);
}

function formatUpdatedAt(value: string | number | Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

function UsageSeriesChart({
  data,
  type,
  index,
  totalCurrent,
}: {
  data: UsageDataPoint[];
  type: string;
  index: number;
  totalCurrent: number;
}) {
  const label = type.charAt(0).toUpperCase() + type.slice(1);
  const displayLabel = label.toUpperCase();
  // The account reference uses one neutral plotting system for every request
  // type. Query-type colors belong to the public data visualizations, not this
  // monochrome account surface.
  const color = "rgb(200 200 204)";
  const chartConfig: ChartConfig = {
    [type]: { label, color },
  };
  const values = data.map((point) =>
    typeof point[type] === "number" ? point[type] as number : 0
  );
  const currentValue = values.at(-1) ?? 0;
  const share = totalCurrent > 0 ? (currentValue / totalCurrent) * 100 : 0;
  const shareLabel = share < 0.1 ? "<0.1% OF TOTAL" : `${share.toFixed(1)}% OF TOTAL`;
  const maxValue = Math.max(...values, 0);
  const yMax = maxValue > 0 ? maxValue * 1.08 : 1;
  const firstTimestamp = data[0]?.timestamp ?? Date.now();
  const lastTimestamp = data.at(-1)?.timestamp ?? firstTimestamp;
  const gradientId = `usage-${type.replace(/[^a-z0-9]/gi, "-")}-${index}`;

  return (
    <section className="usage-series-panel" aria-label={`${displayLabel} request history`}>
      <div className="usage-series-panel__header">
        <div className="usage-series-panel__identity">
          <span style={{ backgroundColor: color }} aria-hidden="true" />
          <h3>{displayLabel}</h3>
        </div>
        <span className="usage-series-panel__share">{shareLabel}</span>
        <strong className="usage-series-panel__current">{currentValue.toLocaleString()}</strong>
      </div>

      <ChartContainer config={chartConfig} className="usage-series-chart">
        <AreaChart data={data} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.28} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="2 6"
            vertical={false}
            stroke="rgb(87 87 90 / 45%)"
          />
          <YAxis
            width={42}
            domain={[0, yMax]}
            tickCount={3}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            allowDecimals={false}
            tickFormatter={(value) => formatCompactNumber(Number(value))}
          />
          <Area
            type="monotone"
            dataKey={type}
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={{ r: 4, fill: color, stroke: "#0e0e10", strokeWidth: 2 }}
            isAnimationActive={false}
          />
          <ChartTooltip
            cursor={{ stroke: "#57575a", strokeDasharray: "3 3" }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const point = payload[0]?.payload as UsageDataPoint;
              const value = typeof point[type] === "number" ? point[type] as number : 0;

              return (
                <div className="usage-chart-tooltip">
                  <span>{formatUpdatedAt(point.timestamp)}</span>
                  <strong style={{ color }}>{value.toLocaleString()}</strong>
                  <small>{label.toUpperCase()} REQUESTS</small>
                </div>
              );
            }}
          />
        </AreaChart>
      </ChartContainer>
      <div className="usage-series-panel__range" aria-hidden="true">
        <span>{formatChartDate(firstTimestamp, "first")}</span>
        <span>{formatChartDate(lastTimestamp, "last")}</span>
      </div>
    </section>
  );
}

function TotalUsageChart({ data, totalCurrent }: { data: UsageDataPoint[]; totalCurrent: number }) {
  const rateTypes = useMemo(() => {
    const types = new Set<string>();
    data.forEach((point) => {
      Object.keys(point).forEach((key) => {
        if (key !== 'date' && key !== 'timestamp' && key !== 'total') {
          types.add(key);
        }
      });
    });
    return Array.from(types);
  }, [data]);

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-chromium-mist">
        No usage data to display
      </div>
    );
  }

  return (
    <div className="usage-series-list" aria-label="Cumulative request history by type">
      {rateTypes.map((type, index) => (
        <UsageSeriesChart key={type} data={data} type={type} index={index} totalCurrent={totalCurrent} />
      ))}
    </div>
  );
}

export function SubTotalUsage({ subscriptionId }: SubTotalUsageProps) {
  const { data: session } = useSession();

  const { data: totalUsageHistory } = trpc.subscription.usage.totalUsageHistory.useQuery(
    { subscriptionId },
    { enabled: !!session && !!subscriptionId }
  );

  return (
    <section className="account-reference-section total-usage-card" aria-labelledby="total-usage-heading">
      <div className="subscription-section-heading" id="total-usage-heading">
        <span>03 TOTAL USAGE · ALL TIME</span>
        {totalUsageHistory?.lastUpdated && (
          <em className="total-usage-card__updated">
            UPDATED {formatUpdatedAt(totalUsageHistory.lastUpdated)}
          </em>
        )}
      </div>

      <div className="total-usage-card__content">
        {!totalUsageHistory || totalUsageHistory.history.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-chromium-mist mb-2">No Usage History Yet</div>
            <p className="text-sm text-chromium-mist/70">
              Usage history will appear here after your first billing period.
            </p>
          </div>
        ) : (
          <div className="total-usage-card__content">
            <div className="usage-total-summary">
              <div>
                <span>ALL REQUEST TYPES</span>
                <strong>{totalUsageHistory.currentTotal.toLocaleString()}</strong>
                <small>TOTAL REQUESTS</small>
              </div>
              <p>
                <span>INDEPENDENT SCALE PER REQUEST TYPE</span>
              </p>
            </div>
            <TotalUsageChart data={totalUsageHistory.history} totalCurrent={totalUsageHistory.currentTotal} />
          </div>
        )}
      </div>
    </section>
  );
}
